import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { getLogoFilename } from 'app/main/util/provider';
import {
	Card,
	CardContent,
	Typography,
	Box
} from '@material-ui/core';
import { getThemeColors } from '../utils/themeUtils';
import ClusterInfoDialog from './ClusterInfoDialog';

const useStyles = makeStyles(theme => ({
	card: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none'
	},
	cardContent: {
		padding: theme.spacing(3),
		'&:last-child': {
			paddingBottom: theme.spacing(3)
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: theme.spacing(3)
	},
	headerText: {
		flex: 1
	},
	title: {
		fontSize: '16px',
		fontWeight: 600,
		color: '#f8fafc',
		marginBottom: '8px'
	},
	subtitle: {
		fontSize: '14px',
		color: '#94a3b8',
		lineHeight: 1.5
	},
	tableContainer: {
		marginTop: theme.spacing(2),
		overflowX: 'auto',
		marginLeft: '-16px',
		marginRight: '-16px',
		[theme.breakpoints.up('sm')]: {
			marginLeft: '-24px',
			marginRight: '-24px'
		}
	},
	tableInner: {
		display: 'inline-block',
		minWidth: '100%',
		paddingTop: '8px',
		paddingBottom: '8px',
		verticalAlign: 'middle',
		paddingLeft: '16px',
		paddingRight: '16px',
		[theme.breakpoints.up('sm')]: {
			paddingLeft: '24px',
			paddingRight: '24px'
		}
	},
	table: {
		width: '90%',
		margin: '0 auto',
		borderCollapse: 'collapse',
		'& thead': {
			'& tr': {
				borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
				'& th': {
					padding: '12px 12px',
					textAlign: 'left',
					fontSize: '12px',
					fontWeight: 600,
					color: '#94a3b8',
					whiteSpace: 'nowrap',
					textTransform: 'uppercase',
					letterSpacing: '0.05em',
					'&:first-child': {
						paddingLeft: '16px',
						[theme.breakpoints.up('sm')]: {
							paddingLeft: 0
						}
					},
					'&:last-child': {
						paddingRight: '16px',
						[theme.breakpoints.up('sm')]: {
							paddingRight: 0
						}
					}
				}
			}
		},
		'& tbody': {
			'& tr': {
				borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
				cursor: 'pointer',
				transition: 'background-color 0.15s ease',
				'&:hover': {
					backgroundColor: 'rgba(255, 255, 255, 0.05)'
				},
				'&:last-child': {
					borderBottom: 'none'
				},
				'& td': {
					padding: '16px 12px',
					fontSize: '14px',
					verticalAlign: 'middle',
					whiteSpace: 'nowrap',
					color: '#94a3b8',
					'&:first-child': {
						paddingLeft: '16px',
						[theme.breakpoints.up('sm')]: {
							paddingLeft: 0
						}
					},
					'&:last-child': {
						paddingRight: '16px',
						textAlign: 'right',
						[theme.breakpoints.up('sm')]: {
							paddingRight: 0
						}
					}
				}
			}
		}
	},
	clusterCell: {
		display: 'flex',
		alignItems: 'center',
		gap: '16px'
	},
	providerIcon: {
		width: '44px',
		height: '44px',
		flexShrink: 0,
		borderRadius: '50%',
		border: '1px solid rgba(255, 255, 255, 0.1)',
		objectFit: 'contain',
		backgroundColor: '#ffffff',
		padding: '4px'
	},
	clusterInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: '4px'
	},
	clusterName: {
		fontWeight: 500,
		color: '#f8fafc',
		fontSize: '14px'
	},
	clusterProvider: {
		color: '#94a3b8',
		fontSize: '14px',
		textTransform: 'capitalize'
	},
	statusBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '2px 10px',
		borderRadius: '6px',
		fontSize: '12px',
		fontWeight: 500,
		boxShadow: '0 0 0 1px currentColor inset'
	},
	statusActive: {
		backgroundColor: 'rgba(34, 197, 94, 0.1)',
		color: '#22c55e'
	},
	statusInactive: {
		backgroundColor: 'rgba(148, 163, 184, 0.1)',
		color: '#94a3b8'
	},
	statusError: {
		backgroundColor: 'rgba(239, 68, 68, 0.1)',
		color: '#ef4444'
	},
	resourceText: {
		color: '#f8fafc',
		fontSize: '14px',
		marginBottom: '4px'
	},
	resourceSubtext: {
		color: '#94a3b8',
		fontSize: '14px'
	},
	viewLink: {
		color: '#1C75D2',
		fontWeight: 500,
		textDecoration: 'none',
		fontSize: '14px',
		cursor: 'pointer',
		'&:hover': {
			color: '#2d8ae0'
		}
	},
	viewLinkDisabled: {
		color: '#64748b',
		fontWeight: 500,
		textDecoration: 'none',
		fontSize: '14px',
		cursor: 'not-allowed',
		opacity: 0.5,
		pointerEvents: 'none'
	}
}));

const getProviderIcon = (provider) => {
	if (!provider) {
		return './assets/images/providers/local.png';
	}
	try {
		const filename = getLogoFilename(provider);
		if (filename.includes('product-image-placeholder')) {
			return './assets/images/providers/local.png';
		}
		const path = `./assets/images/providers/${filename}`;
		return path;
	} catch (error) {
		console.error('[getProviderIcon] Error:', error);
		return './assets/images/providers/local.png';
	}
};

const getStatusClass = (status, classes) => {
	switch (status?.toLowerCase()) {
		case 'available': // deployed/active
		case 'deploying':
		case 'starting':
			return classes.statusActive;
		case 'failed':
		case 'offline':
			return classes.statusError;
		default:
			return classes.statusInactive;
	}
};

const getStatusStyle = (status, colors) => {
	const state = status?.toLowerCase();
	if (state === 'available' || state === 'deploying' || state === 'starting') {
		return {
			backgroundColor: 'transparent',
			color: colors.status.active,
			border: `1px solid ${colors.status.active}`
		};
	}
	if (state === 'failed' || state === 'offline') {
		return {
			backgroundColor: 'transparent',
			color: colors.status.error,
			border: `1px solid ${colors.status.error}`
		};
	}
	return {
		backgroundColor: 'transparent',
		color: colors.status.inactive,
		border: `1px solid ${colors.status.inactive}`
	};
};

const getStatusLabel = (status) => {
	const statusLower = status?.toLowerCase();
	if (statusLower === 'available') {
		return 'Active';
	}
	if (statusLower === 'deploying' || statusLower === 'starting') {
		return 'Starting';
	}
	if (statusLower === 'failed' || statusLower === 'offline') {
		return 'Error';
	}
	if (statusLower === 'stopping' || statusLower === 'destroying') {
		return 'Stopping';
	}
	return 'Inactive';
};

const ClusterOverviewTable = ({ clusters, embedded = false, themeMode = 'dark' }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);
	const [selectedCluster, setSelectedCluster] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);

	const handleClusterClick = (cluster) => {
		setSelectedCluster(cluster);
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setSelectedCluster(null);
	};

	if (clusters && clusters.length > 0) {
	}

	if (!clusters || clusters.length === 0) {
		const content = (
			<>
				<div className={classes.header}>
					<div className={classes.headerText}>
						{!embedded && (
							<Typography className={classes.title} style={{ color: colors.text.primary }}>
								Clusters
							</Typography>
						)}
						<Typography className={classes.subtitle} style={{ color: colors.text.secondary }}>
							No clusters available at the moment
						</Typography>
					</div>
				</div>
			</>
		);

		if (embedded) {
			return <div style={{ padding: '24px' }}>{content}</div>;
		}

		return (
			<Card className={classes.card} style={{ backgroundColor: colors.card.background, border: `1px solid ${colors.card.border}` }}>
				<CardContent className={classes.cardContent}>
					{content}
				</CardContent>
			</Card>
		);
	}

	// Sort clusters: active first, then inactive
	const sortedClusters = [...clusters].sort((a, b) => {
		const aState = (a.state || '').toLowerCase();
		const bState = (b.state || '').toLowerCase();
		
		const aIsActive = aState === 'available' || aState === 'deploying' || aState === 'starting';
		const bIsActive = bState === 'available' || bState === 'deploying' || bState === 'starting';
		
		// Active clusters first
		if (aIsActive && !bIsActive) return -1;
		if (!aIsActive && bIsActive) return 1;
		
		// Within same status, sort by name
		return (a.name || '').localeCompare(b.name || '');
	});
	
	// Show all clusters when embedded, otherwise limit to 5
	const displayClusters = embedded ? sortedClusters : sortedClusters.slice(0, 5);

	const tableContent = (
		<>
			{!embedded && (
				<div className={classes.header}>
					<div className={classes.headerText}>
						<Typography className={classes.title} style={{ color: colors.text.primary }}>
							Clusters
						</Typography>
						<Typography className={classes.subtitle} style={{ color: colors.text.secondary }}>
							A list of all clusters in your account including their name, status, and resources.
						</Typography>
					</div>
				</div>
			)}

			<div className={classes.tableContainer}>
				<table className={classes.table} style={{ borderColor: colors.border.primary }}>
					<thead>
						<tr>
							<th style={{ color: colors.text.secondary }}>Name</th>
							<th style={{ color: colors.text.secondary }}>Status</th>
							<th style={{ color: colors.text.secondary }}>Resources</th>
							<th>
								<span style={{ visibility: 'hidden' }}>Actions</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{displayClusters.map((cluster) => (
								<tr 
									key={cluster.clusterId || cluster._id} 
									style={{ borderTopColor: colors.border.primary }}
									onClick={() => handleClusterClick(cluster)}
								>
									<td>
										<div className={classes.clusterCell}>
											<img
												src={getProviderIcon(cluster.provider)}
												alt={cluster.provider}
												className={classes.providerIcon}
												onError={(e) => {
													console.error('[ClusterOverviewTable] Image failed to load:', e.target.src);
												}}
											/>
											<div className={classes.clusterInfo}>
												<div className={classes.clusterName} style={{ color: colors.text.primary }}>{cluster.clusterName || cluster.name}</div>
												<div className={classes.clusterProvider} style={{ color: colors.text.secondary }}>{cluster.provider}</div>
											</div>
										</div>
									</td>
									<td>
										<span 
											className={`${classes.statusBadge} ${getStatusClass(cluster.state, classes)}`}
											style={getStatusStyle(cluster.state, colors)}
										>
											{getStatusLabel(cluster.state)}
										</span>
									</td>
									<td>
										<div>
											<div className={classes.resourceText} style={{ color: colors.text.primary }}>
												{cluster.nodeCount || 0} Node{cluster.nodeCount !== 1 ? 's' : ''}
											</div>
											<div className={classes.resourceSubtext} style={{ color: colors.text.secondary }}>
												{cluster.gpuCount || 0} GPU{cluster.gpuCount !== 1 ? 's' : ''} • CPU {cluster.cpuUtilization?.toFixed(1) || 0}%
											</div>
										</div>
									</td>
									<td>
										{(() => {
											const state = (cluster.state || '').toLowerCase();
											const isActive = state === 'available' || state === 'deploying' || state === 'starting';
											
											if (isActive) {
												return (
													<a
														href={`/clusters/${cluster.cluster_id || cluster._id}`}
														className={classes.viewLink}
														style={{ color: colors.accent.blue }}
													>
														View
													</a>
												);
											} else {
												return (
													<span className={classes.viewLinkDisabled} style={{ color: colors.text.muted }}>
														View
													</span>
												);
											}
										})()}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
		</>
	);

	if (embedded) {
		return (
			<>
				<div style={{ padding: '16px' }}>{tableContent}</div>
				<ClusterInfoDialog
					open={dialogOpen}
					onClose={handleDialogClose}
					cluster={selectedCluster}
					themeMode={themeMode}
				/>
			</>
		);
	}

	return (
		<>
			<Card className={classes.card} style={{ backgroundColor: colors.card.background, border: `1px solid ${colors.card.border}` }}>
				<CardContent className={classes.cardContent}>
					{tableContent}
				</CardContent>
			</Card>
			<ClusterInfoDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				cluster={selectedCluster}
				themeMode={themeMode}
			/>
		</>
	);
};

export default ClusterOverviewTable;
